import type { MetricTrend, StatsMetric } from '@fluenceur/types';
import { ComparisonPeriodType } from '@fluenceur/types';

/**
 * Returns true when a trend has no meaningful previous-period data to compare against
 * (e.g. the metric is brand new with previousValue = 0).
 */
export const isNewMetric = (trend?: MetricTrend): boolean =>
  trend?.previousValue === 0 && trend.direction === 'up';

/**
 * Get translated comparison text based on period type.
 * Returns undefined when no trend or no meaningful comparison exists.
 *
 * Never falls back to `trend.label`: that deprecated field carries the percentage
 * itself ("+8.5% vs last month"), which `TrendBadge` already renders beside this
 * text, so echoing it printed the change twice.
 */
export const getComparisonText = (
  metric: StatsMetric,
  t: (key: string, options?: { count?: number }) => string
): string | undefined => {
  if (!metric.trend || isNewMetric(metric.trend)) return undefined;

  const { periodType, periodDays } = metric.trend;

  switch (periodType) {
    case ComparisonPeriodType.THIS_MONTH:
      return t('dashboard.comparison.vs_this_month');
    case ComparisonPeriodType.LAST_7_DAYS:
      return t('dashboard.comparison.vs_last_7_days');
    case ComparisonPeriodType.LAST_30_DAYS:
      return t('dashboard.comparison.vs_last_30_days');
    case ComparisonPeriodType.LAST_90_DAYS:
      return t('dashboard.comparison.vs_last_90_days');
    case ComparisonPeriodType.YEAR_TO_DATE:
      return t('dashboard.comparison.vs_ytd');
    case ComparisonPeriodType.CUSTOM:
      return periodDays
        ? t('dashboard.comparison.vs_last_n_days', { count: periodDays })
        : t('dashboard.comparison.vs_previous_period');
    default:
      return t('dashboard.comparison.vs_previous_period');
  }
};
